import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Campaigns from "@/pages/Campaigns";
import CampaignWizard from "@/pages/CampaignWizard";
import CampaignDetail from "@/pages/CampaignDetail";
import Agents from "@/pages/Agents";
import AgentDetail from "@/pages/AgentDetail";
import Contacts from "@/pages/Contacts";
import Calls from "@/pages/Calls";
import CallDetail from "@/pages/CallDetail";
import LiveCalls from "@/pages/LiveCalls";
import Leads from "@/pages/Leads";
import Callbacks from "@/pages/Callbacks";
import PhoneNumbers from "@/pages/PhoneNumbers";
import DoNotCall from "@/pages/DoNotCall";
import Analytics from "@/pages/Analytics";
import DailyReport from "@/pages/DailyReport";
import Costs from "@/pages/Costs";
import Quality from "@/pages/Quality";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import AuditLog from "@/pages/AuditLog";
import ProviderCenter from "@/pages/ProviderCenter";
import CrmIntegrations from "@/pages/CrmIntegrations";
import Reconciliation from "@/pages/Reconciliation";
import ContactLists from "@/pages/ContactLists";
import ContactListDetail from "@/pages/ContactListDetail";
import ImportJobs from "@/pages/ImportJobs";
import Segments from "@/pages/Segments";
import ExportJobs from "@/pages/ExportJobs";
import LeadDetail from "@/pages/LeadDetail";
import Setup from "@/pages/Setup";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <p className="text-sm text-slate-500">Loading workspace…</p>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Guard({ resource, children }) {
  const { can } = useAuth();
  if (!can(resource, "read"))
    return (
      <div className="panel p-6" data-testid="no-permission">
        <h2 className="text-lg font-semibold text-slate-900">No access</h2>
        <p className="mt-1 text-sm text-slate-500">
          Your role does not have permission to view this section.
        </p>
      </div>
    );
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            element={
              <Protected>
                <AppShell />
              </Protected>
            }
          >
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Guard resource="dashboard"><Dashboard /></Guard>} />
            <Route path="/campaigns" element={<Guard resource="campaigns"><Campaigns /></Guard>} />
            <Route path="/campaigns/new" element={<Guard resource="campaigns"><CampaignWizard /></Guard>} />
            <Route path="/campaigns/:id" element={<Guard resource="campaigns"><CampaignDetail /></Guard>} />
            <Route path="/agents" element={<Guard resource="agents"><Agents /></Guard>} />
            <Route path="/agents/:id" element={<Guard resource="agents"><AgentDetail /></Guard>} />
            <Route path="/contacts" element={<Guard resource="contacts"><Contacts /></Guard>} />
            <Route path="/contacts/:id" element={<Guard resource="contacts"><Contacts /></Guard>} />
            <Route path="/lists" element={<Guard resource="lists"><ContactLists /></Guard>} />
            <Route path="/lists/segments" element={<Guard resource="lists"><Segments /></Guard>} />
            <Route path="/lists/import-jobs" element={<Guard resource="lists"><ImportJobs /></Guard>} />
            <Route path="/lists/export-jobs" element={<Guard resource="exports"><ExportJobs /></Guard>} />
            <Route path="/leads/:id" element={<Guard resource="leads"><LeadDetail /></Guard>} />
            <Route path="/lists/import-jobs" element={<Guard resource="lists"><ImportJobs /></Guard>} />
            <Route path="/lists/:id" element={<Guard resource="lists"><ContactListDetail /></Guard>} />
            <Route path="/calls" element={<Guard resource="calls"><Calls /></Guard>} />
            <Route path="/calls/:id" element={<Guard resource="calls"><CallDetail /></Guard>} />
            <Route path="/live-calls" element={<Guard resource="live_calls"><LiveCalls /></Guard>} />
            <Route path="/leads" element={<Guard resource="leads"><Leads /></Guard>} />
            <Route path="/leads/:id" element={<Guard resource="leads"><Leads /></Guard>} />
            <Route path="/callbacks" element={<Guard resource="callbacks"><Callbacks /></Guard>} />
            <Route path="/phone-numbers" element={<Guard resource="phone_numbers"><PhoneNumbers /></Guard>} />
            <Route path="/do-not-call" element={<Guard resource="dnc"><DoNotCall /></Guard>} />
            <Route path="/analytics" element={<Guard resource="analytics"><Analytics /></Guard>} />
            <Route path="/reports" element={<Guard resource="analytics"><DailyReport /></Guard>} />
            <Route path="/costs" element={<Guard resource="costs"><Costs /></Guard>} />
            <Route path="/quality" element={<Guard resource="quality"><Quality /></Guard>} />
            <Route path="/users" element={<Guard resource="users"><Users /></Guard>} />
            <Route path="/setup" element={<Guard resource="settings"><Setup /></Guard>} />
            <Route path="/provider-center" element={<Guard resource="providers"><ProviderCenter /></Guard>} />
            <Route path="/crm" element={<Guard resource="settings"><CrmIntegrations /></Guard>} />
            <Route path="/reconciliation" element={<Guard resource="settings"><Reconciliation /></Guard>} />
            <Route path="/settings" element={<Guard resource="settings"><Settings /></Guard>} />
            <Route path="/audit-log" element={<Guard resource="audit"><AuditLog /></Guard>} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}